import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { observer } from 'mobx-react';
import { Button, Grid, LinearProgress } from '@material-ui/core';
import { getTodayStr } from '@ourbranch/date-fns-helpers';
import { AgentPaymentOption, getAgentPaymentPresets } from '@ourbranch/policy-utils';

import { Label } from 'core';
import BaseDialog from 'core/components/base-dialog';
import { Card } from 'core/components/card';
import Field from 'core/components/form/form.v2';
import { useToast } from 'core/components/toast';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import { awsDateToDateFormatter, currencyFormatter } from 'core/helpers/formatters';
import icon from 'core/assets/svg/alert-white-bold.svg';
import useStyles from '../payment.styles';

/**
 * Agent payments on CFM / Forreston (#36683). An agent (no CanManuallyChargePolicy) chooses what to
 * pay instead of typing an amount: the amount due now, the next installment or the remaining balance,
 * built from the policy's billing schedule (getAgentPaymentPresets, which staff-billing-details also
 * uses to refuse any other amount). A confirmation step shows the amount, payment method and policy
 * before the charge. Paying the next installment early works like Pay Ahead (see runAgentPayment).
 */

const PRESET_NAMES = {
  [AgentPaymentOption.AmountDue]: 'Amount due now',
  [AgentPaymentOption.NextInstallment]: 'Next installment',
  [AgentPaymentOption.RemainingBalance]: 'Remaining balance'
};

const presetLabel = ({ option, amount, dueDate }) =>
  `${PRESET_NAMES[option]}: ${currencyFormatter(amount)}${dueDate ? ` (due ${awsDateToDateFormatter(dueDate)})` : ''}`;

const validationSchema = Yup.object().shape({
  option: Yup.string().required('Choose what to pay'),
  paymentMethod: Yup.string().required('Payment method required'),
  internalDescription: Yup.string().required('Description required')
});

const AgentPaymentForm = observer(function AgentPaymentForm({
  paymentMethodOptions,
  sweepPaymentMethodIds,
  paymentMethodFieldProps,
  sweepSetupHint,
  renderCreditCardFeeNote
}) {
  const classes = useStyles();
  const toast = useToast();
  const {
    user: { username }
  } = useSession();
  const {
    account: {
      fetchFullAccountAndPolicy,
      policies: {
        policy: { policy, billingDetails, hasBillingHold, billingHoldBlocksManualPayments, runAgentPayment }
      }
    }
  } = useStore();
  const [pendingPayment, setPendingPayment] = useState(null);
  const [loading, setLoading] = useState(false);

  const presets = getAgentPaymentPresets({
    nextPaymentDate: billingDetails?.nextPaymentDate,
    nextPaymentAmount: billingDetails?.nextPaymentTotal ?? billingDetails?.nextPaymentAmount,
    nextPaymentBilled: billingDetails?.nextPaymentBilled,
    nextPaymentDueDate: billingDetails?.nextPaymentDueDate,
    // A Pay Ahead would overwrite the active hold; GO's schedule nets it without one (#37421).
    payAheadAllowed: !hasBillingHold || Boolean(billingDetails?.legacyImportedTerm),
    totalRemaining: billingDetails?.totalRemaining,
    today: getTodayStr(policy.state)
  });

  if (!presets.length) {
    return (
      <div className={classes.manualChargeContainer}>
        <Label type="coverageValue">Nothing is due on this policy.</Label>
      </div>
    );
  }

  const presetFor = (option) => presets.find((preset) => preset.option === option);
  const methodLabelFor = (id) => paymentMethodOptions.find((method) => method.id === id)?.value;

  const charge = async () => {
    const { preset, paymentMethod, internalDescription } = pendingPayment;
    setLoading(true);
    const res = await runAgentPayment(policy, {
      paymentMethod,
      sweepPaymentMethodId: sweepPaymentMethodIds.has(paymentMethod) ? paymentMethod : undefined,
      preset: { option: preset.option, amount: preset.amount },
      internalDescription: `Manual payment made by ${username} (${PRESET_NAMES[
        preset.option
      ].toLowerCase()}). ${internalDescription.trim()}`
    });
    // Charged but the Pay Ahead hold failed: keep the form disabled so the installment isn't paid twice
    // (as the Pay Ahead modal does); the refresh below reloads the tab.
    setLoading(!res.ok && !!res.chargeSuccess);
    setPendingPayment(null);
    if (res.ok) {
      toast.notify({
        type: 'success',
        message: `The payment of ${currencyFormatter(preset.amount)} was billed to ${res.paymentMethod} ending in ${res.last4}`
      });
    } else {
      toast.notify({ type: 'error', message: res.error || 'There was an error during the payment.' });
    }
    // A failed hold after a successful charge still changed the balance, so refresh either way.
    if (res.ok || res.chargeSuccess) {
      fetchFullAccountAndPolicy(policy.id, policy.id.slice(0, 9));
    }
  };

  return (
    <>
      <div className={classes.manualChargeContainer}>
        <Grid container alignItems="center" justifyContent="flex-start" className={classes.rowTitle}>
          <Grid item xs={6}>
            <Label type="coverageValue">Make a payment</Label>
          </Grid>
        </Grid>
        {billingHoldBlocksManualPayments && (
          <Grid item>
            <Card className={classes.smallContainer}>
              <Grid className={classes.centeredContentContainer}>
                <img src={icon} className={classes.smallIcon} alt="Notification Icon" />
                You must remove the payment hold before making a manual charge.
              </Grid>
            </Card>
          </Grid>
        )}
        <Formik
          initialValues={{ option: presets[0].option, paymentMethod: '', internalDescription: '' }}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={(values) => setPendingPayment({ ...values, preset: presetFor(values.option) })}
        >
          {({ handleSubmit, values }) => (
            <div className={classes.manualChargeForm}>
              {!billingHoldBlocksManualPayments && renderCreditCardFeeNote?.(values.paymentMethod)}
              <Grid container key="row1" spacing={4}>
                <Grid item xs={6}>
                  <Field
                    id="option"
                    name="option"
                    type="radio"
                    mode="dark"
                    options={presets.map((preset) => ({ value: preset.option, label: presetLabel(preset) }))}
                    optionsSideBySide={false}
                    ignoreGlobalDisabledState
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    name="paymentMethod"
                    type="select"
                    label="Payment Method"
                    xs={12}
                    mode="dark"
                    options={paymentMethodOptions}
                    {...paymentMethodFieldProps}
                    ignoreGlobalDisabledState
                  />
                  {sweepSetupHint && <div className={classes.sweepAccountHintRow}>{sweepSetupHint}</div>}
                </Grid>
              </Grid>
              <Grid container key="row2" spacing={4} className={classes.spaceBetweenRow}>
                <Field name="internalDescription" type="string" label="Description" xs={8} ignoreGlobalDisabledState />
                <Button
                  variant="contained"
                  color="secondary"
                  className={classes.submit}
                  onClick={handleSubmit}
                  disabled={loading || billingHoldBlocksManualPayments}
                >
                  Review Payment
                </Button>
              </Grid>
            </div>
          )}
        </Formik>
      </div>
      <BaseDialog open={!!pendingPayment} onClose={() => !loading && setPendingPayment(null)}>
        {pendingPayment && (
          <div>
            <Label type="h1">Confirm payment</Label>
            <ul>
              <li>Policy: {policy.id}</li>
              <li>Amount: {presetLabel(pendingPayment.preset)}</li>
              <li>Payment method: {methodLabelFor(pendingPayment.paymentMethod)}</li>
            </ul>
            <Grid container justifyContent="flex-end" spacing={2}>
              <Grid item>
                <Button variant="text" color="secondary" onClick={() => setPendingPayment(null)} disabled={loading}>
                  Back
                </Button>
              </Grid>
              <Grid item>
                <Button variant="contained" color="secondary" onClick={charge} disabled={loading}>
                  Confirm Payment
                </Button>
              </Grid>
            </Grid>
            {loading && <LinearProgress color="secondary" className={classes.loadingBar} />}
          </div>
        )}
      </BaseDialog>
    </>
  );
});

AgentPaymentForm.propTypes = {
  paymentMethodOptions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, value: PropTypes.string }))
    .isRequired,
  sweepPaymentMethodIds: PropTypes.instanceOf(Set).isRequired,
  paymentMethodFieldProps: PropTypes.object,
  sweepSetupHint: PropTypes.node,
  renderCreditCardFeeNote: PropTypes.func
};

AgentPaymentForm.defaultProps = {
  paymentMethodFieldProps: {},
  sweepSetupHint: null,
  renderCreditCardFeeNote: undefined
};

export default AgentPaymentForm;
